/**
 * LiveTransactionFlow - Real-time transaction flow dashboard widget
 * Shows live activity from all touchpoints (POS, Online, Kiosk, WhatsApp)
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Activity, Monitor, Globe, Tablet, Phone, MessageCircle,
  DollarSign, TrendingUp, Clock, Coffee,
  ShoppingBag, Zap, RefreshCw
} from 'lucide-react';
import { api } from '@/utils/apiClient';

// Touchpoint configurations
const TOUCHPOINTS = {
  pos: { 
    icon: Monitor, 
    label: 'POS', 
    color: 'bg-blue-500',
    lightBg: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-600 dark:text-blue-400'
  },
  online: { 
    icon: Globe, 
    label: 'Online', 
    color: 'bg-green-500',
    lightBg: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-600 dark:text-green-400'
  },
  kiosk: { 
    icon: Tablet, 
    label: 'Kiosk', 
    color: 'bg-purple-500',
    lightBg: 'bg-purple-100 dark:bg-purple-900/30',
    textColor: 'text-purple-600 dark:text-purple-400'
  },
  server_tablet: { 
    icon: Tablet, 
    label: 'Server', 
    color: 'bg-orange-500',
    lightBg: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-600 dark:text-orange-400'
  },
  phone: { 
    icon: Phone, 
    label: 'Phone', 
    color: 'bg-pink-500',
    lightBg: 'bg-pink-100 dark:bg-pink-900/30',
    textColor: 'text-pink-600 dark:text-pink-400'
  },
  whatsapp: { 
    icon: MessageCircle, 
    label: 'WhatsApp', 
    color: 'bg-emerald-500',
    lightBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    textColor: 'text-emerald-600 dark:text-emerald-400'
  },
  coffee_pass: { 
    icon: Coffee, 
    label: 'Coffee Pass', 
    color: 'bg-amber-500',
    lightBg: 'bg-amber-100 dark:bg-amber-900/30',
    textColor: 'text-amber-600 dark:text-amber-400'
  },
};

export default function LiveTransactionFlow() {
  const [data, setData] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sourceRes, recentRes] = await Promise.all([
        api.get('/admin/transactions/by-source'),
        api.get('/admin/transactions/recent?limit=5')
      ]);
      
      setData(sourceRes.data);
      setRecentTransactions(recentRes.data.transactions || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to fetch transaction flow:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds for near real-time feel
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const breakdown = data?.breakdown || [];
  const totalRevenue = data?.total_gross_revenue || 0;
  const totalTransactions = data?.total_transactions || 0;

  // Get top 3 performing channels
  const topChannels = [...breakdown].sort((a, b) => b.gross_revenue - a.gross_revenue).slice(0, 3);

  return (
    <div 
      className="bg-card rounded-2xl border border-border overflow-hidden"
      data-testid="live-transaction-flow"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 dark:from-indigo-500/20 dark:via-purple-500/20 dark:to-pink-500/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Transaction Flow</h3>
              <p className="text-xs text-muted-foreground">All touchpoints combined</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span>Live</span>
            </div>
            <button 
              onClick={fetchData}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Stats */}
      <div className="p-6 space-y-6">
        {/* Total Revenue Banner */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 opacity-80" />
              <span className="text-sm font-medium opacity-90">Total Revenue</span>
            </div>
            <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
            <div className="text-xs opacity-75 mt-1">All time from all channels</div>
          </div>
          <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingBag className="w-4 h-4 opacity-80" />
              <span className="text-sm font-medium opacity-90">Transactions</span>
            </div>
            <div className="text-2xl font-bold">{totalTransactions}</div>
            <div className="text-xs opacity-75 mt-1">Total orders processed</div>
          </div>
        </div>

        {/* Top Performing Channels - Only show when multiple sources have revenue */}
        {topChannels.length > 1 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-foreground">Top Channels</span>
            </div>
            <div className="space-y-2">
              {topChannels.map((channel, index) => {
              const config = TOUCHPOINTS[channel.source] || TOUCHPOINTS.pos;
              const Icon = config.icon;
              const percentage = totalRevenue > 0 ? (channel.gross_revenue / totalRevenue * 100) : 0;
              
              return (
                <div 
                  key={channel.source}
                  className={`flex items-center gap-3 p-3 rounded-xl ${config.lightBg} transition-all hover:scale-[1.02]`}
                >
                  <div className={`p-2 ${config.color} rounded-lg`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${config.textColor}`}>
                        {config.label}
                      </span>
                      <span className="font-bold text-foreground">
                        ${channel.gross_revenue.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                      <span>{channel.transaction_count} orders</span>
                      <span className="font-medium">{percentage.toFixed(1)}%</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${config.color} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* All Channels Grid */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-medium text-foreground">All Channels</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(TOUCHPOINTS).map(([key, config]) => {
              const channelData = breakdown.find(b => b.source === key);
              const Icon = config.icon;
              const revenue = channelData?.gross_revenue || 0;
              const count = channelData?.transaction_count || 0;
              
              return (
                <div 
                  key={key}
                  className={`p-3 rounded-xl border border-border bg-card hover:border-primary/30 transition-all ${
                    revenue > 0 ? 'opacity-100' : 'opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 ${config.color} rounded-md`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-medium text-foreground truncate">
                      {config.label}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-foreground">
                    ${revenue.toFixed(0)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {count} orders
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        {recentTransactions.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-foreground">Recent Activity</span>
            </div>
            <div className="space-y-2">
              {recentTransactions.slice(0, 4).map((txn, index) => {
                const config = TOUCHPOINTS[txn.source] || TOUCHPOINTS.pos;
                const Icon = config.icon;
                const time = new Date(txn.created_at);
                const timeStr = time.toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true 
                });
                
                return (
                  <div 
                    key={txn.transaction_id || index}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className={`p-1.5 ${config.color} rounded-md`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground truncate">
                          {txn.type === 'coffee_pass' ? 'Coffee Pass' : 'Order'} #{txn.order_id?.slice(-6) || 'N/A'}
                        </span>
                        <span className="text-sm font-bold text-foreground">
                          ${(txn.total || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {config.label} • {timeStr}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Last Updated */}
        {lastUpdate && (
          <div className="text-center text-xs text-muted-foreground">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}
